// ===========================================
// APP.TSX - Main Application Entry
// ===========================================

import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store';
import { NotificationToast, ErrorBoundary } from './components/ui';
import CookieConsent from './components/legal/CookieConsent';
import PremiumMainLayout from './components/layout/PremiumMainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import AuthCallback from './pages/auth/AuthCallback';
import AuthSsoPage from './pages/auth/AuthSsoPage';

// Auth Flow Pages (lazy loaded)
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));

// Billing Pages (lazy loaded)
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const BillingSuccessPage = lazy(() => import('./pages/billing/BillingSuccessPage'));
const BillingCancelPage = lazy(() => import('./pages/billing/BillingCancelPage'));

// Main Pages
import PremiumDashboardPage from './pages/dashboard/PremiumDashboardPage';
import PremiumContactsPage from './pages/contacts/PremiumContactsPage';
import PremiumCampaignsPage from './pages/campaigns/PremiumCampaignsPage';
import EmailsPage from './pages/emails/EmailsPage';
const EmailEditorPage = lazy(() => import('./pages/emails/EmailEditorPage'));
import PremiumAnalyticsPage from './pages/analytics/PremiumAnalyticsPage';
import PremiumSettingsPage from './pages/settings/PremiumSettingsPage';

// New Feature Pages (lazy loaded for performance)
const SocialMediaPage = lazy(() => import('./pages/social/SocialMediaPage'));
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage'));
const AutomationPage = lazy(() => import('./pages/automation/AutomationPage'));
const SMSMarketingPage = lazy(() => import('./pages/sms/SMSMarketingPage'));
const LandingPagesPage = lazy(() => import('./pages/landing/LandingPagesPage'));
const PublicLandingPage = lazy(() => import('./pages/landing/PublicLandingPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const DeleteAccountPage = lazy(() => import('./pages/legal/DeleteAccountPage'));
const WhatsAppPage = lazy(() => import('./pages/whatsapp/WhatsAppPage'));
const CommissionsPage = lazy(() => import('./pages/commissions/CommissionsPage'));
const ReferralsPage = lazy(() => import('./pages/referrals/ReferralsPage'));

// Phase 2 Feature Pages (Payments, Invoices, Bookings, Users)
const PaymentsPage = lazy(() => import('./pages/payments/PaymentsPage'));
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
const BookingsPage = lazy(() => import('./pages/bookings/BookingsPage'));
const UserManagementPage = lazy(() => import('./pages/users/UserManagementPage'));

// Phase 3 Feature Pages (AI, Testing, API Docs)
const PremiumAIPage = lazy(() => import('./pages/ai/PremiumAIPage'));
const TestingPage = lazy(() => import('./pages/testing/TestingPage'));
const ApiDocsPage = lazy(() => import('./pages/api-docs/ApiDocsPage'));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));
const MessagesPage = lazy(() => import('./pages/messages/MessagesPage'));

// Super Admin Dashboard (Platform-wide management)
const SuperAdminDashboard = lazy(() => import('./pages/admin/SuperAdminDashboard'));

// Loading component for lazy routes
const PageLoader = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

// App Component

const App: React.FC = () => {
  const { isAuthenticated, initialize, _hasHydrated } = useAuthStore();

  // Initialize auth state from localStorage once on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Wait for hydration before rendering routes
  if (!_hasHydrated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        {/* Global Notifications */}
        <NotificationToast />
        <CookieConsent />
        
        <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } 
        />
        <Route 
          path="/register" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
          } 
        />
        <Route 
          path="/auth/callback" 
          element={<AuthCallback />} 
        />
        <Route
          path="/auth/sso"
          element={<AuthSsoPage />}
        />
        
        {/* Email Verification */}
        <Route 
          path="/verify-email" 
          element={
            <Suspense fallback={<PageLoader />}>
              <VerifyEmailPage />
            </Suspense>
          } 
        />
        
        {/* Password Reset Flow */}
        <Route 
          path="/reset-password" 
          element={
            <Suspense fallback={<PageLoader />}>
              <ResetPasswordPage />
            </Suspense>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <Suspense fallback={<PageLoader />}>
              <ForgotPasswordPage />
            </Suspense>
          } 
        />
        
        {/* Billing Pages (Public for checkout callbacks) */}
        <Route 
          path="/billing/success" 
          element={
            <Suspense fallback={<PageLoader />}>
              <BillingSuccessPage />
            </Suspense>
          } 
        />
        <Route 
          path="/billing/cancel" 
          element={
            <Suspense fallback={<PageLoader />}>
              <BillingCancelPage />
            </Suspense>
          } 
        />

        {/* Public published landing pages (lead capture) */}
        <Route
          path="/p/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicLandingPage />
            </Suspense>
          }
        />

        {/* Public legal pages (store review + in-app links) */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<PageLoader />}>
              <PrivacyPage />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<PageLoader />}>
              <TermsPage />
            </Suspense>
          }
        />
        <Route
          path="/delete-account"
          element={
            <Suspense fallback={<PageLoader />}>
              <DeleteAccountPage />
            </Suspense>
          }
        />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <PremiumMainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<PremiumDashboardPage />} />
          <Route path="/contacts" element={<PremiumContactsPage />} />
          <Route path="/campaigns" element={<PremiumCampaignsPage />} />
          <Route path="/emails" element={<EmailsPage />} />
          <Route path="/analytics" element={<PremiumAnalyticsPage />} />
          <Route path="/settings" element={<PremiumSettingsPage />} />
          
          {/* NEW FEATURE ROUTES */}
          <Route path="/social" element={
            <Suspense fallback={<PageLoader />}>
              <SocialMediaPage />
            </Suspense>
          } />
          <Route path="/leads" element={
            <Suspense fallback={<PageLoader />}>
              <LeadsPage />
            </Suspense>
          } />
          <Route path="/automation" element={
            <Suspense fallback={<PageLoader />}>
              <AutomationPage />
            </Suspense>
          } />
          <Route path="/sms" element={
            <Suspense fallback={<PageLoader />}>
              <SMSMarketingPage />
            </Suspense>
          } />
          <Route path="/landing-pages" element={
            <Suspense fallback={<PageLoader />}>
              <LandingPagesPage />
            </Suspense>
          } />
          <Route path="/whatsapp" element={
            <Suspense fallback={<PageLoader />}>
              <WhatsAppPage />
            </Suspense>
          } />
          
          {/* PHASE 2 FEATURE ROUTES */}
          <Route path="/payments" element={
            <Suspense fallback={<PageLoader />}>
              <PaymentsPage />
            </Suspense>
          } />
          <Route path="/invoices" element={
            <Suspense fallback={<PageLoader />}>
              <InvoicesPage />
            </Suspense>
          } />
          <Route path="/bookings" element={
            <Suspense fallback={<PageLoader />}>
              <BookingsPage />
            </Suspense>
          } />
          <Route path="/commissions" element={
            <Suspense fallback={<PageLoader />}>
              <CommissionsPage />
            </Suspense>
          } />
          <Route path="/referrals" element={
            <Suspense fallback={<PageLoader />}>
              <ReferralsPage />
            </Suspense>
          } />
          <Route path="/users" element={
            <Suspense fallback={<PageLoader />}>
              <UserManagementPage />
            </Suspense>
          } />
          <Route path="/notifications" element={
            <Suspense fallback={<PageLoader />}>
              <NotificationsPage />
            </Suspense>
          } />
          <Route path="/messages" element={
            <Suspense fallback={<PageLoader />}>
              <MessagesPage />
            </Suspense>
          } />
          
          {/* PHASE 3 FEATURE ROUTES - AI, Testing, API Docs */}
          <Route path="/ai" element={
            <Suspense fallback={<PageLoader />}>
              <PremiumAIPage />
            </Suspense>
          } />
          <Route path="/testing" element={
            <Suspense fallback={<PageLoader />}>
              <TestingPage />
            </Suspense>
          } />
          <Route path="/api-docs" element={
            <Suspense fallback={<PageLoader />}>
              <ApiDocsPage />
            </Suspense>
          } />
          
          {/* BILLING - Protected */}
          <Route path="/billing" element={
            <Suspense fallback={<PageLoader />}>
              <BillingPage />
            </Suspense>
          } />
        </Route>
        
        {/* SUPER ADMIN ROUTE - Platform-wide management */}
        <Route 
          path="/super-admin" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <SuperAdminDashboard />
              </Suspense>
            </ProtectedRoute>
          } 
        />

        {/* EMAIL EDITOR - Fullscreen, no sidebar layout */}
        <Route
          path="/emails/editor/:id"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <EmailEditorPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        
        {/* Redirect root to dashboard or login */}
        <Route 
          path="/" 
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} 
        />
        
        {/* 404 - Redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
